/**
 * React hook exposing the cached Lexiun JWT identity (`userId` +
 * `orgId` + `scopes` + admin flags) for components that need to
 * pass them through to cascade-gated RPCs (the F3 briefing button)
 * or gate admin UI surfaces (Phase 8 — `<ActivityBar>` + Org
 * admin route).
 *
 * Backed by:
 *  - `getCurrentUserId` + `getCurrentOrgId` (re-exported via
 *    `@/lib/lexserver-client`)
 *  - `getCurrentScopes` (direct from `@/lib/lexiun-jwt` — also
 *    re-exported from `lexserver-client` for parity)
 *
 * All three trigger an auth-exchange round-trip on first call and
 * then read from a module-level cache, so the hook is cheap to call
 * from multiple components.
 *
 * Return shape: `{ identity, status }`.
 *  - `identity` is `null` until the token has been fetched (or if
 *    the auth exchange failed). Consumers that just need the
 *    resolved values can ignore `status` and `identity != null`
 *    behaves the same as the legacy boolean check.
 *  - `status` is `"loading" | "ready" | "error"`. `OrgAdminLayout`
 *    uses it to distinguish a slow auth-exchange from an outright
 *    failure (Clerk session expired mid-render, network blip);
 *    other consumers can keep null-checking `identity`.
 */

import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import {
  getCurrentOrgId,
  getCurrentUserId,
} from "@/lib/lexserver-client";
import { getCurrentScopes } from "@/lib/lexiun-jwt";

export interface AuthIdentity {
  userId: string;
  orgId: string;
  /** Scopes claim from the Lexiun JWT. Empty array when the claim
   *  is absent. Phase 8: consumers gate admin UI on
   *  `isOrgAdmin` / `isPlatformAdmin` rather than reading the
   *  raw scope strings, but the array is also exposed for any
   *  future fine-grained gates. */
  scopes: string[];
  /** Convenience: true when the JWT carries `operator.admin` or
   *  `platform.admin`. Matches lexserver's `requireOrgAdmin()`
   *  middleware — see lexserver/src/auth/jwt-auth.ts:122. */
  isOrgAdmin: boolean;
  /** True when the JWT carries `platform.admin` specifically.
   *  Used by surfaces that are platform-admin-only (e.g. cross-org
   *  feedback browse — but that lives in lexportal, not here). */
  isPlatformAdmin: boolean;
}

/**
 * Pure derivation of the admin booleans from the JWT scopes claim.
 * Extracted so the rules are unit-testable without mounting React.
 *
 * Truth table:
 *  - scopes contains "platform.admin"               → both flags true
 *  - scopes contains "operator.admin" only          → isOrgAdmin true,
 *                                                     isPlatformAdmin false
 *  - scopes contains neither (or is empty / missing) → both false
 *
 * Mirrors lexserver's `requireOrgAdmin()` predicate at
 * lexserver/src/auth/jwt-auth.ts:122.
 */
export function deriveAdminFlags(scopes: readonly string[]): {
  isOrgAdmin: boolean;
  isPlatformAdmin: boolean;
} {
  const isPlatformAdmin = scopes.includes("platform.admin");
  const isOrgAdmin = isPlatformAdmin || scopes.includes("operator.admin");
  return { isOrgAdmin, isPlatformAdmin };
}

export type AuthIdentityStatus = "loading" | "ready" | "error";

export interface UseAuthIdentityResult {
  identity: AuthIdentity | null;
  status: AuthIdentityStatus;
}

export function useAuthIdentity(): UseAuthIdentityResult {
  const [identity, setIdentity] = useState<AuthIdentity | null>(null);
  const [status, setStatus] = useState<AuthIdentityStatus>("loading");

  useEffect(() => {
    let cancelled = false;
    Promise.all([getCurrentUserId(), getCurrentOrgId(), getCurrentScopes()])
      .then(([userId, orgId, scopes]) => {
        if (cancelled) return;
        const { isOrgAdmin, isPlatformAdmin } = deriveAdminFlags(scopes);
        setIdentity({ userId, orgId, scopes, isOrgAdmin, isPlatformAdmin });
        setStatus("ready");
        // Tag the current Sentry scope so any subsequent browser event
        // carries the resolved identity. Per-tab (SPA) scope — cleared
        // on Sentry.setUser(null) below when identity is torn down.
        Sentry.setUser({ id: userId });
        Sentry.setTag("org_id", orgId);
      })
      .catch(() => {
        if (cancelled) return;
        // Auth exchange failed — surface "error" so OrgAdminLayout
        // can show "Falha na autenticação. Recarregue a página."
        // instead of looping on the loading spinner. Other consumers
        // keep checking `identity != null` and degrade gracefully.
        setStatus("error");
      });
    return () => {
      cancelled = true;
      Sentry.setUser(null);
    };
  }, []);

  return { identity, status };
}
